import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  ArrowRight,
  Check,
  AlertCircle,
  CreditCard,
  Calendar,
  FileText,
  Tag,
  Info
} from 'lucide-react'
import {
  processRefundThunk,
  getRefundInstallmentsThunk
} from '../store/slices/plotCancellationSlice'

// Pakistani Rupee formatter
const formatPakistaniCurrency = amount => {
  if (amount === null || amount === undefined) return 'PKR 0'
  return `PKR ${Number(amount).toLocaleString('en-PK')}`
}

const ProcessRefundModal = ({
  cancelPlotId,
  onClose,
  themeColor = '#0d9488'
}) => {
  const dispatch = useDispatch()
  const { selectedPlotDetails, loading, refundInstallments } = useSelector(
    state =>
      state.cancelPlot || {
        selectedPlotDetails: null,
        loading: false,
        refundInstallments: []
      }
  )

  // Get the installments array properly
  // First determine if refundInstallments is nested array or direct array
  const installments = React.useMemo(() => {
    if (!refundInstallments) return []

    if (Array.isArray(refundInstallments)) {
      if (refundInstallments.length === 0) return []

      if (Array.isArray(refundInstallments[0])) {
        return refundInstallments[0] // It's the first element of the array
      } else if (Array.isArray(refundInstallments[1])) {
        return refundInstallments[1] // It's the second element of the array
      } else {
        return refundInstallments // It's a direct array
      }
    }

    return []
  }, [refundInstallments])

  // Filter to only show unpaid or partially paid installments based on the schema
  const pendingInstallments = React.useMemo(() => {
    return installments.filter(
      inst => inst?.status === 'pending' || inst?.status === 'partial'
    )
  }, [installments])

  // Find the first pending installment
  const firstPendingInstallment = React.useMemo(() => {
    return pendingInstallments.length > 0 ? pendingInstallments[0] : null
  }, [pendingInstallments])

  // Calculate the total amount of all pending installments
  const totalPendingAmount = React.useMemo(() => {
    return pendingInstallments.reduce((sum, inst) => {
      const remainingAmount = inst.amount - (inst.paid_amount || 0)
      return sum + remainingAmount
    }, 0)
  }, [pendingInstallments])

  const isMultiInstallmentPayment = React.useRef(false)

  const [formData, setFormData] = useState({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'Bank Transfer',
    transactionReference: '', // This field will serve as receipt_no
    installmentId: ''
  })

  const [formError, setFormError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccessIndicator, setShowSuccessIndicator] = useState(false)

  // Populate the amount field with the first pending installment amount on initial load
  useEffect(() => {
    if (firstPendingInstallment && !formData.amount) {
      // For partially paid installments, show the remaining amount
      const remainingAmount =
        firstPendingInstallment.amount -
        (firstPendingInstallment.paid_amount || 0)

      setFormData(prev => ({
        ...prev,
        amount: remainingAmount.toString() || '',
        installmentId: firstPendingInstallment._id
      }))
    }
  }, [firstPendingInstallment])

  const handleChange = e => {
    const { name, value } = e.target

    // If we're changing the amount, check if it's a multi-installment payment
    if (name === 'amount') {
      const parsedAmount = parseFloat(value)
      if (!isNaN(parsedAmount)) {
        // Get selected installment
        const selectedInstallment = installments.find(
          inst => inst._id === formData.installmentId
        )

        if (selectedInstallment) {
          const remainingAmount =
            selectedInstallment.amount - (selectedInstallment.paid_amount || 0)

          // Mark as multi-installment payment if amount exceeds this installment
          isMultiInstallmentPayment.current = parsedAmount > remainingAmount
        }
      }
    }

    setFormData({
      ...formData,
      [name]: value
    })
    setFormError('')
  }

  // Handle installment selection
  const handleInstallmentChange = e => {
    const installmentId = e.target.value
    const selectedInstallment = installments.find(
      inst => inst._id === installmentId
    )

    if (selectedInstallment) {
      // Calculate the remaining amount for this installment
      const remainingAmount =
        selectedInstallment.amount - (selectedInstallment.paid_amount || 0)

      setFormData({
        ...formData,
        installmentId,
        amount: remainingAmount.toString() || ''
      })

      // Reset the multi-installment flag
      isMultiInstallmentPayment.current = false
    }
  }

  const handleSubmit = e => {
    e.preventDefault()
    setIsSubmitting(true)

    // Validation
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setFormError('Please enter a valid amount greater than 0')
      setIsSubmitting(false)
      return
    }

    if (!formData.installmentId) {
      setFormError('Please select an installment to pay')
      setIsSubmitting(false)
      return
    }

    // Check if transaction reference is provided
    if (!formData.transactionReference) {
      setFormError('Please enter a transaction reference for tracking purposes')
      setIsSubmitting(false)
      return
    }

    if (!cancelPlotId || typeof cancelPlotId !== 'string') {
      setFormError('Invalid Cancel Plot ID')
      setIsSubmitting(false)
      return
    }

    // Check if amount exceeds total remaining balance
    if (
      selectedPlotDetails &&
      parseFloat(formData.amount) > selectedPlotDetails.remaining_balance
    ) {
      setFormError(
        `Amount cannot exceed total remaining balance (${formatPakistaniCurrency(
          selectedPlotDetails.remaining_balance
        )})`
      )
      setIsSubmitting(false)
      return
    }

    // Get selected installment to verify
    const selectedInstallment = installments.find(
      inst => inst._id === formData.installmentId
    )

    if (selectedInstallment) {
      const remainingAmount =
        selectedInstallment.amount - (selectedInstallment.paid_amount || 0)
      const parsedAmount = parseFloat(formData.amount)

      // Only show warning if amount exceeds this installment but not all pending installments
      if (parsedAmount > remainingAmount && parsedAmount > totalPendingAmount) {
        setFormError(
          `Amount cannot exceed total pending installments (${formatPakistaniCurrency(
            totalPendingAmount
          )})`
        )
        setIsSubmitting(false)
        return
      }
    }

    try {
      // Create the payload for the API call
      const paymentData = {
        cancelPlotId: cancelPlotId,
        installmentId: formData.installmentId,
        amount: parseFloat(formData.amount),
        paymentDate: formData.paymentDate,
        paymentMethod: formData.paymentMethod,
        transactionReference: formData.transactionReference,
        // Optional: specifically set receipt_no to match transactionReference
        // This is redundant since our backend now handles this
        receipt_no: formData.transactionReference
      }

      // console.log('Submitting payment data:', paymentData)

      // Dispatch the action to process the refund using the thunk
      dispatch(processRefundThunk(paymentData))
        .unwrap()
        .then(result => {
          // console.log('Payment successful:', result)
          setIsSubmitting(false)
          setShowSuccessIndicator(true)

          setTimeout(() => {
            // Refresh data after payment is successful
            dispatch(getRefundInstallmentsThunk(cancelPlotId))

            // Close modal after success animation
            onClose()
          }, 1500)
        })
        .catch(error => {
          console.error('Payment error:', error)
          setFormError(error.message || 'Failed to process refund')
          setIsSubmitting(false)
        })
    } catch (error) {
      console.error('Form submission error:', error)
      setFormError(error.message || 'Failed to process refund')
      setIsSubmitting(false)
    }
  }

  // Payment method options with icons
  const paymentMethods = [
    {
      value: 'Bank Transfer',
      label: 'Bank Transfer',
      icon: <ArrowRight size={16} />
    },
    { value: 'Check', label: 'Check', icon: <FileText size={16} /> },
    { value: 'Cash', label: 'Cash', icon: <Tag size={16} /> },
    { value: 'UPI', label: 'UPI', icon: <ArrowRight size={16} /> },
    {
      value: 'Credit Card',
      label: 'Credit Card',
      icon: <CreditCard size={16} />
    }
  ]

  // Glass effect styles
  const glassEffect =
    'backdrop-blur-md bg-opacity-20 border border-slate-700 border-opacity-40'

  // Show multi-installment info message when amount exceeds current installment
  const showMultiInstallmentMessage = React.useMemo(() => {
    if (!formData.installmentId || !formData.amount) return false

    const parsedAmount = parseFloat(formData.amount)
    if (isNaN(parsedAmount)) return false

    const selectedInstallment = installments.find(
      inst => inst._id === formData.installmentId
    )

    if (selectedInstallment) {
      const remainingAmount =
        selectedInstallment.amount - (selectedInstallment.paid_amount || 0)
      return parsedAmount > remainingAmount
    }

    return false
  }, [formData.amount, formData.installmentId, installments])

  return (
    <div className='fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4 backdrop-blur-sm'>
      <div
        className={`${
          showSuccessIndicator ? 'scale-105' : 'scale-100'
        } transition-all duration-300 w-full max-w-xl overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 shadow-xl ${glassEffect}`}
      >
        {/* Header */}
        <div className='p-4 border-b border-slate-700 border-opacity-50'>
          <h2 className='text-xl font-bold text-teal-300 flex items-center'>
            <CreditCard className='mr-2' size={20} />
            Process Refund Payment
          </h2>
          <p className='text-slate-400 text-sm'>
            Complete the details to process the refund
          </p>
        </div>

        {showSuccessIndicator ? (
          <div className='p-8 flex flex-col items-center justify-center'>
            <div className='w-16 h-16 rounded-full bg-teal-500 flex items-center justify-center mb-4 animate-pulse'>
              <Check size={32} className='text-white' />
            </div>
            <h3 className='text-teal-300 text-xl font-bold'>
              Refund Processed!
            </h3>
            <p className='text-slate-400 text-center mt-2'>
              Your refund has been successfully processed.
            </p>
          </div>
        ) : (
          <div className='p-4 space-y-4 max-h-[70vh] overflow-y-auto'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              {/* Remaining Balance */}
              <div className='md:col-span-2 bg-slate-800 bg-opacity-50 rounded-xl p-3 border border-slate-700 transform transition-all duration-200 hover:shadow-lg'>
                <div className='flex items-center justify-between'>
                  <span className='text-slate-400 text-sm'>
                    Remaining Balance
                  </span>
                  <div className='text-xl font-bold text-teal-300'>
                    {selectedPlotDetails
                      ? formatPakistaniCurrency(
                          selectedPlotDetails.remaining_balance
                        )
                      : 'Loading...'}
                  </div>
                </div>
              </div>

              {/* Total Pending Installments */}
              <div className='md:col-span-2 bg-slate-800 bg-opacity-50 rounded-xl p-3 border border-slate-700 transform transition-all duration-200 hover:shadow-lg'>
                <div className='flex items-center justify-between'>
                  <span className='text-slate-400 text-sm'>
                    Total Pending Installments
                  </span>
                  <div className='text-xl font-bold text-blue-300'>
                    {formatPakistaniCurrency(totalPendingAmount)}
                  </div>
                </div>
              </div>

              {/* Error message */}
              {formError && (
                <div className='md:col-span-2 p-3 bg-red-900 bg-opacity-20 border border-red-700 rounded-xl flex items-start space-x-3 text-red-300 text-sm animate-pulse'>
                  <AlertCircle size={18} className='mt-0.5 flex-shrink-0' />
                  <span>{formError}</span>
                </div>
              )}

              {/* Multi-installment payment message */}
              {showMultiInstallmentMessage && (
                <div className='md:col-span-2 p-3 bg-blue-900 bg-opacity-20 border border-blue-700 rounded-xl flex items-start space-x-3 text-blue-300 text-sm'>
                  <Info size={18} className='mt-0.5 flex-shrink-0' />
                  <span>
                    The amount exceeds this installment's remaining balance.
                    Payment will be applied across multiple installments in
                    order of due date.
                  </span>
                </div>
              )}

              {/* Debug Info - Comment out in production */}
              {/* <div className="md:col-span-2 text-xs text-slate-500">
                <details>
                  <summary>Debug Info</summary>
                  <div>cancelPlotId: {cancelPlotId || "undefined"}</div>
                  <div>
                    pendingInstallments count: {pendingInstallments.length}
                  </div>
                </details>
              </div> */}

              {/* Installment Selection */}
              <div className='md:col-span-2 space-y-1'>
                <label
                  htmlFor='installmentId'
                  className='block text-sm font-medium text-teal-300 flex items-center'
                >
                  <Calendar size={16} className='mr-2' />
                  Select Installment *
                </label>
                {pendingInstallments.length > 0 ? (
                  <div className='relative'>
                    <select
                      name='installmentId'
                      id='installmentId'
                      value={formData.installmentId}
                      onChange={handleInstallmentChange}
                      required
                      className='focus:ring-teal-500 focus:border-teal-500 block w-full px-4 py-2 bg-slate-800 bg-opacity-50 border border-slate-700 text-teal-100 rounded-xl shadow-inner transition-all duration-200 hover:bg-slate-700 hover:bg-opacity-30 appearance-none'
                    >
                      <option value='' className='bg-slate-800'>
                        Select an installment
                      </option>
                      {pendingInstallments.map((inst, idx) => {
                        const remainingAmount =
                          inst.amount - (inst.paid_amount || 0)
                        const statusLabel =
                          inst.status === 'partial' ? '(Partially Paid)' : ''

                        return (
                          <option
                            key={inst._id}
                            value={inst._id}
                            className='bg-slate-800'
                          >
                            Installment #{idx + 1} -{' '}
                            {formatPakistaniCurrency(remainingAmount)}{' '}
                            {statusLabel} (Due:{' '}
                            {new Date(inst.due_date).toLocaleDateString()})
                          </option>
                        )
                      })}
                    </select>
                    <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
                      <ArrowRight size={16} className='text-teal-500' />
                    </div>
                  </div>
                ) : (
                  <div className='text-yellow-300 text-sm p-2 bg-yellow-900 bg-opacity-20 rounded-xl'>
                    No pending installments found.
                  </div>
                )}
              </div>

              {/* Amount Input */}
              <div className='space-y-1'>
                <label
                  htmlFor='amount'
                  className='block text-sm font-medium text-teal-300 flex items-center'
                >
                  <Tag size={16} className='mr-2' />
                  Amount *
                </label>
                <div className='relative'>
                  <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
                    <span className='text-slate-400 text-sm'>PKR</span>
                  </div>
                  <input
                    type='number'
                    name='amount'
                    id='amount'
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    placeholder='0.00'
                    step='0.01'
                    className='focus:ring-teal-500 focus:border-teal-500 block w-full pl-12 pr-4 py-2 bg-slate-800 bg-opacity-50 border border-slate-700 text-teal-100 rounded-xl shadow-inner transition-all duration-200 hover:bg-slate-700 hover:bg-opacity-30'
                  />
                </div>
              </div>

              {/* Payment Date */}
              <div className='space-y-1'>
                <label
                  htmlFor='paymentDate'
                  className='block text-sm font-medium text-teal-300 flex items-center'
                >
                  <Calendar size={16} className='mr-2' />
                  Payment Date *
                </label>
                <input
                  type='date'
                  name='paymentDate'
                  id='paymentDate'
                  value={formData.paymentDate}
                  onChange={handleChange}
                  required
                  className='focus:ring-teal-500 focus:border-teal-500 block w-full px-4 py-2 bg-slate-800 bg-opacity-50 border border-slate-700 text-teal-100 rounded-xl shadow-inner transition-all duration-200 hover:bg-slate-700 hover:bg-opacity-30'
                />
              </div>

              {/* Payment Method */}
              <div className='space-y-1'>
                <label
                  htmlFor='paymentMethod'
                  className='block text-sm font-medium text-teal-300 flex items-center'
                >
                  <CreditCard size={16} className='mr-2' />
                  Payment Method *
                </label>
                <div className='relative'>
                  <select
                    name='paymentMethod'
                    id='paymentMethod'
                    value={formData.paymentMethod}
                    onChange={handleChange}
                    required
                    className='focus:ring-teal-500 focus:border-teal-500 block w-full px-4 py-2 bg-slate-800 bg-opacity-50 border border-slate-700 text-teal-100 rounded-xl shadow-inner transition-all duration-200 hover:bg-slate-700 hover:bg-opacity-30 appearance-none'
                  >
                    {paymentMethods.map(method => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                  <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
                    <ArrowRight size={16} className='text-teal-500' />
                  </div>
                </div>
              </div>

              {/* Transaction Reference - Now marked as required */}
              <div className='md:col-span-2 space-y-1'>
                <label
                  htmlFor='transactionReference'
                  className='block text-sm font-medium text-teal-300 flex items-center'
                >
                  <FileText size={16} className='mr-2' />
                  Transaction Reference / Receipt Number *
                </label>
                <input
                  type='text'
                  name='transactionReference'
                  id='transactionReference'
                  value={formData.transactionReference}
                  onChange={handleChange}
                  required
                  placeholder='Transaction ID, Check #, Receipt No, etc.'
                  className='focus:ring-teal-500 focus:border-teal-500 block w-full px-4 py-2 bg-slate-800 bg-opacity-50 border border-slate-700 text-teal-100 rounded-xl shadow-inner transition-all duration-200 hover:bg-slate-700 hover:bg-opacity-30'
                />
                <p className='text-xs text-slate-400 mt-1'>
                  This will be used as your receipt number for tracking
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className='flex justify-end space-x-3 pt-2'>
              <button
                type='button'
                onClick={onClose}
                className='px-4 py-2 rounded-xl border border-slate-600 text-slate-300 transition-all duration-200 hover:bg-slate-700 hover:text-white'
              >
                Cancel
              </button>
              <button
                type='submit'
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  !formData.installmentId ||
                  !formData.transactionReference
                }
                className={`px-6 py-2 rounded-xl flex items-center space-x-2 transition-all duration-200 
                ${
                  isSubmitting ||
                  !formData.installmentId ||
                  !formData.transactionReference
                    ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                    : `bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:shadow-lg hover:from-teal-400 hover:to-teal-500`
                }`}
              >
                {isSubmitting ? (
                  <>
                    <span className='animate-spin h-4 w-4 border-2 border-white border-opacity-50 border-t-white rounded-full mr-2'></span>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    <span>Process Payment</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProcessRefundModal
